import React, { useState, useEffect } from 'react';
import { Plus, Search, Eye, Edit, Check, X, Clock, FileText, User, Euro, Calendar } from 'lucide-react';
import { db } from '../firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import useAccounting from '../hooks/useAccounting';
import useDevisActions from '../hooks/useDevisActions';
import { formaterEuro, TARIFS_REFERENCE } from '../services/tarificationService';
import { formatAddress } from '../utils/addressFormatter';
import { STATUTS } from '../services/accountingService';
import CreateDevisModal from './CreateDevisModal';
import EditDevisModal from './EditDevisModal';
import DevisActionsPanel from './DevisActionsPanel';
import '../Devis.css';

/**
 * Composant de gestion complète des devis
 */
const DevisManagement = ({ openModal, closeModal }) => {
  const {
    devis,
    loading,
    error,
    rechargerDonnees,
    accepterNouveauDevis,
    refuserNouveauDevis,
    devisEnCours,
    devisEnvoyes,
    devisAcceptes
  } = useAccounting(db);

  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDevis, setSelectedDevis] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [devisAvecActions, setDevisAvecActions] = useState(null);
  const [devisAEditer, setDevisAEditer] = useState(null);
  const [showSimpleTest, setShowSimpleTest] = useState(false);

  // Hook pour les actions de devis
  const { actions, loading: actionsLoading, error: actionsError } = useDevisActions();

  // Fonction pour synchroniser l'email du client avant une action d'envoi
  const getLatestClientEmail = async (devis) => {
    if (!devis.clientId) return devis.clientEmail;

    try {
      const clientDoc = await getDoc(doc(db, 'clients', devis.clientId));
      if (clientDoc.exists()) {
        const clientData = clientDoc.data();
        if (clientData.email) {
          // Si l'email est différent, on met à jour le devis pour la prochaine fois
          if (clientData.email !== devis.clientEmail) {
            console.log(`Synchronisation email: ${devis.clientEmail} -> ${clientData.email}`);
            try {
              await updateDoc(doc(db, 'devis', devis.id), {
                clientEmail: clientData.email
              });
            } catch (err) {
              console.warn("Erreur mise à jour devis email:", err);
            }
          }
          return clientData.email;
        }
      }
    } catch (err) {
      console.error("Erreur récupération dernier email client:", err);
    }
    return devis.clientEmail;
  };

  // Filtrer les devis selon le statut et la recherche
  const filteredDevis = devis.filter(devis => {
    const matchesSearch =
      devis.clientNom.toLowerCase().includes(searchTerm.toLowerCase()) ||
      devis.numero.toLowerCase().includes(searchTerm.toLowerCase());

    if (!matchesSearch) return false;

    switch (filterStatus) {
      case 'brouillon':
        return devis.statut === STATUTS.DEVIS.BROUILLON;
      case 'envoye':
        return devis.statut === STATUTS.DEVIS.ENVOYE;
      case 'accepte':
        return devis.statut === STATUTS.DEVIS.ACCEPTE;
      case 'refuse':
        return devis.statut === STATUTS.DEVIS.REFUSE;
      case 'all':
      default:
        return true;
    }
  });

  // Gestionnaire d'actions pour les devis
  const handleActionDevis = async (actionId, devis, options = {}) => {
    try {
      let result;

      switch (actionId) {
        case 'accepter':
          result = await actions.accepter(devis, {
            acceptePar: 'utilisateur',
            notes: 'Accepté depuis l\'interface de gestion'
          });
          if (result.conversionEffectuee) {
            alert(`✅ Devis accepté ! Le prospect ${result.devis.clientNom} a été converti en client actif.`);
          } else {
            alert('✅ Devis accepté !');
          }
          // Fermer le modal d'actions après l'acceptation
          setDevisAvecActions(null);
          break;

        case 'refuser':
          const raison = prompt('Raison du refus (optionnel):');
          if (raison !== null) {
            result = await actions.refuser(devis, {
              raison: raison || 'Non spécifiée',
              notes: 'Refusé depuis l\'interface de gestion'
            });
            alert('❌ Devis refusé');
            // Fermer le modal d'actions après le refus
            setDevisAvecActions(null);
          }
          break;

        case 'envoyer':
          const emailAjour = await getLatestClientEmail(devis);
          let emailDestinataire = emailAjour;

          // Si toujours pas d'email, demander à l'utilisateur
          if (!emailDestinataire) {
            const saisieEmail = prompt('⚠️ Aucune adresse email trouvée pour ce client (ni sur le devis, ni sur la fiche).\nVeuillez saisir l\'adresse email du destinataire :');
            if (!saisieEmail) return; // Annulation ou vide
            emailDestinataire = saisieEmail.trim();
          }

          // Confirmer l'envoi
          if (confirm(`Confirmez-vous l'envoi du devis à ${emailDestinataire} ?`)) {
            result = await actions.envoyer(devis, {
              ...options,
              destinataire: emailDestinataire
            });
            alert('📧 Devis envoyé avec succès');
            setDevisAvecActions(null);
          }
          break;

        case 'creer_facture':
          result = await actions.creerFacture(devis, options);
          alert('💰 Facture créée avec succès');
          break;

        case 'dupliquer':
          result = await actions.dupliquer(devis, options);
          alert('📋 Devis dupliqué avec succès');
          // Fermer le modal d'actions après la duplication
          setDevisAvecActions(null);
          break;

        case 'telecharger':
          await actions.telechargerPDF(devis, options);
          break;

        case 'imprimer':
          await actions.imprimer(devis, options);
          break;

        case 'archiver':
          result = await actions.archiver(devis, options);
          alert('📁 Devis archivé avec succès');
          break;

        case 'supprimer':
          if (window.confirm('Êtes-vous sûr de vouloir supprimer définitivement ce devis ?')) {
            result = await actions.supprimer(devis, options);
            alert('🗑️ Devis supprimé avec succès');
            // Fermer le modal d'actions après suppression
            setDevisAvecActions(null);
          }
          break;

        case 'relancer':
          const emailRelance = await getLatestClientEmail(devis);
          if (!emailRelance) {
            alert("❌ Impossible d'envoyer la relance : aucune adresse email trouvée pour ce client.");
            return;
          }
          result = await actions.relancerClient(devis, { ...options, destinataire: emailRelance });
          alert('🔔 Relance envoyée avec succès');
          // Fermer le modal d'actions après la relance
          setDevisAvecActions(null);
          break;

        case 'envoyer_rappel':
          const emailRappel = await getLatestClientEmail(devis);
          if (!emailRappel) {
            alert("❌ Impossible d'envoyer le rappel : aucune adresse email trouvée pour ce client.");
            return;
          }
          result = await actions.executerActionPersonnalisee('envoyer_rappel', devis, { ...options, destinataire: emailRappel });
          alert('🔔 Rappel envoyé avec succès');
          break;

        case 'reouvrir':
          result = await actions.reouvrir(devis, options);
          alert('🔄 Devis réouvert avec succès');
          // Fermer le modal d'actions après la réouverture
          setDevisAvecActions(null);
          break;

        case 'renouveler':
          result = await actions.renouveler(devis, options);
          alert('🔄 Devis renouvelé avec succès');
          // Fermer le modal d'actions après la réouverture
          setDevisAvecActions(null);
          break;

        case 'negocier':
          const montantPropose = prompt('Montant proposé pour la négociation (€):', devis.montantTTC);
          if (montantPropose !== null) {
            const conditions = prompt('Conditions de la négociation (optionnel):', devis.conditionsPaiement);
            result = await actions.negocier(devis, {
              montantPropose: parseFloat(montantPropose) || devis.montantTTC,
              conditions: conditions || devis.conditionsPaiement,
              notes: 'Négociation ouverte depuis l\'interface'
            });
            alert('🤝 Négociation ouverte avec succès');
            // Fermer le modal d'actions après l'ouverture de négociation
            setDevisAvecActions(null);
          }
          break;

        case 'partager':
          // Implémentation simplifiée pour le partage
          const utilisateurs = prompt('Entrez les emails des utilisateurs à partager (séparés par des virgules):');
          if (utilisateurs) {
            const emails = utilisateurs.split(',').map(email => email.trim());
            result = await actions.partager(devis, emails.map(email => ({ email })), options);
            alert('🔗 Devis partagé avec succès');
          }
          break;

        case 'voir':
          // Fermer le modal d'actions avant d'ouvrir le modal de détails
          setDevisAvecActions(null);
          setSelectedDevis(devis);
          break;

        case 'modifier':
          // Ouvrir le modal de modification
          setDevisAvecActions(null);
          setDevisAEditer(devis);
          setShowEditModal(true);
          break;

        default:
          console.warn('Action non gérée:', actionId);
          return;
      }

      // Recharger les données après une action qui modifie l'état
      if (['accepter', 'refuser', 'envoyer', 'creer_facture', 'dupliquer', 'archiver', 'supprimer', 'reouvrir', 'renouveler', 'negocier'].includes(actionId)) {
        rechargerDonnees();
      }

    } catch (error) {
      console.error(`Erreur lors de l'action ${actionId}:`, error);
      alert(`❌ Erreur lors de l'action: ${error.message}`);
    }
  };

  // Obtenir la couleur du statut
  const getStatutColor = (statut) => {
    switch (statut) {
      case STATUTS.DEVIS.BROUILLON:
        return 'bg-gray-100 text-gray-800';
      case STATUTS.DEVIS.ENVOYE:
        return 'bg-blue-100 text-blue-800';
      case STATUTS.DEVIS.ACCEPTE:
        return 'bg-green-100 text-green-800';
      case STATUTS.DEVIS.REFUSE:
        return 'bg-red-100 text-red-800';
      case STATUTS.DEVIS.EXPIRE:
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Obtenir le libellé du statut
  const getStatutLabel = (statut) => {
    switch (statut) {
      case STATUTS.DEVIS.BROUILLON:
        return 'Brouillon';
      case STATUTS.DEVIS.ENVOYE:
        return 'Envoyé';
      case STATUTS.DEVIS.ACCEPTE:
        return 'Accepté';
      case STATUTS.DEVIS.REFUSE:
        return 'Refusé';
      case STATUTS.DEVIS.EXPIRE:
        return 'Expiré';
      default:
        return statut;
    }
  };

  // Composant de détail de devis
  const DetailDevis = ({ devis, onClose }) => {
    const [employees, setEmployees] = useState([]);
    const [loadingEmployees, setLoadingEmployees] = useState(false);

    // Fonction pour récupérer le type de tarif de façon robuste (cohérente avec DevisModalBase)
    const getBaseRateTypeFromDevis = (devis) => {
      // Ordre de priorité pour récupérer le type de tarif :
      // 1. Depuis les paramètres du contrat sauvegardés
      if (devis.contratParams?.baseRateType) {
        console.log('Modal détail - Type de tarif récupéré depuis contratParams:', devis.contratParams.baseRateType);
        return devis.contratParams.baseRateType;
      }

      // 2. Depuis la configuration des calculs
      if (devis.calculs?.configuration?.baseRateType) {
        console.log('Modal détail - Type de tarif récupéré depuis calculs.configuration:', devis.calculs.configuration.baseRateType);
        return devis.calculs.configuration.baseRateType;
      }

      // 3. Depuis le contrat des calculs
      if (devis.calculs?.contrat?.baseRateType) {
        console.log('Modal détail - Type de tarif récupéré depuis calculs.contrat:', devis.calculs.contrat.baseRateType);
        return devis.calculs.contrat.baseRateType;
      }

      // 4. Fallback : analyser le tarif de base pour déduire le type
      if (devis.calculs?.contrat?.tarifBase) {
        const tarifBase = devis.calculs.contrat.tarifBase;
        // Si le tarif est proche de 17.17€, c'est probablement exonéré 70+
        if (Math.abs(tarifBase - 17.17) < 0.1) {
          console.log('Modal détail - Type de tarif déduit depuis le tarif de base (17.17€): 70plus');
          return '70plus';
        }
        // Si le tarif est proche de 24.41€, c'est standard
        if (Math.abs(tarifBase - 24.41) < 0.1) {
          console.log('Modal détail - Type de tarif déduit depuis le tarif de base (24.41€): standard');
          return 'standard';
        }
      }

      console.warn('Modal détail - Impossible de récupérer le type de tarif, utilisation du défaut: standard');
      return 'standard';
    };

    // Charger les employés depuis Firestore
    useEffect(() => {
      const loadEmployees = async () => {
        if (!devis.calculs?.lignes) return;

        setLoadingEmployees(true);
        try {
          const { collection, getDocs } = await import('firebase/firestore');
          const employeesCollection = collection(db, 'employees');
          const snapshot = await getDocs(employeesCollection);

          const employeesData = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));

          setEmployees(employeesData);
        } catch (error) {
          console.error('Erreur chargement employés:', error);
        } finally {
          setLoadingEmployees(false);
        }
      };

      loadEmployees();
    }, [devis]);

    // Fonction pour obtenir le prénom du collaborateur depuis la table employees
    const getPrenomCollaborateur = (employeeId) => {
      if (!employeeId) return 'Non assigné';

      // Chercher dans les employés chargés
      const employee = employees.find(emp => emp.id === employeeId);
      if (employee?.firstName) {
        return employee.firstName;
      }

      // Fallback : chercher dans les données du devis
      if (devis.calculs?.lignes) {
        const ligne = devis.calculs.lignes.find(l => l.employeeId === employeeId);
        if (ligne?.collaborateurNom) {
          return ligne.collaborateurNom.split(' ')[0];
        }
      }

      if (devis.calculs?.collaborateurs) {
        const collaborateur = devis.calculs.collaborateurs.find(c => c.id === employeeId);
        if (collaborateur?.nom) {
          return collaborateur.nom.split(' ')[0];
        }
      }

      return 'Non assigné';
    };

    // Fonction pour obtenir les détails des collaborateurs utilisés
    const getCollaborateursUtilises = () => {
      const collaborateurs = new Set();

      // Ajouter les collaborateurs de la liste principale
      if (devis.calculs?.collaborateurs) {
        devis.calculs.collaborateurs.forEach(collab => {
          if (collab.id) collaborateurs.add(collab.id);
        });
      }

      // Ajouter les collaborateurs attribués dans les lignes
      if (devis.calculs?.lignes) {
        devis.calculs.lignes.forEach(ligne => {
          if (ligne.employeeId) collaborateurs.add(ligne.employeeId);
        });
      }

      // Retourner le nombre de collaborateurs uniques
      return Array.from(collaborateurs);
    };

    // Fonction pour obtenir les totaux par collaborateur
    const getTotauxParCollaborateur = () => {
      if (!devis.calculs?.totaux?.parCollaborateur) return [];
      return devis.calculs.totaux.parCollaborateur || [];
    };

    return (
      <div className="devis-modal-container">
        <div className="devis-modal-content devis-fade-in">
          <div className="devis-modal-header">
            <h3 className="devis-modal-title">Détail complet du devis {devis.numero}</h3>
            <button onClick={onClose} className="devis-modal-close">
              <X size={24} />
            </button>
          </div>

          <div className="devis-modal-body">
            {/* Informations client */}
            <div className="devis-section">
              <h4 className="devis-section-title">
                <User size={16} />
                Informations client
              </h4>
              <div className="devis-grid devis-grid-2">
                <div><strong>Nom:</strong> {devis.clientNom}</div>
                <div><strong>Email:</strong> {devis.clientEmail}</div>
                <div><strong>Téléphone:</strong> {devis.clientTelephone}</div>
                <div><strong>Adresse:</strong> {formatAddress(devis.clientAdresse)}</div>
                {devis.calculs?.client?.type && (
                  <div><strong>Type:</strong> {devis.calculs.client.type === 'client' ? 'Client actif' : 'Prospect'}</div>
                )}
              </div>
            </div>

            {/* Type de tarif unifié - Section principale */}
            {(() => {
              const baseRateType = getBaseRateTypeFromDevis(devis);
              const isExonere = baseRateType === '70plus';

              // Essayer de déterminer si c'est une prestation spécifique (Multi-Services)
              let typePrestationSpecifique = null;
              let tarifSpecifique = null;

              // Si on a des lignes calculées, on regarde la première pour voir le type
              if (devis.calculs?.lignes && devis.calculs.lignes.length > 0) {
                // On cherche une correspondance inverse du label vers le type
                // ou on utilise une propriété 'typePrestationKey' si on l'avait ajoutée (ce qui serait mieux pour la prochaine fois)
                // Pour l'instant on va comparer avec les taux de référence pour trouver le match
                const firstLine = devis.calculs.lignes[0];
                const tauxUtilise = firstLine.tarifCesuUnitaire; // Le taux réellement utilisé (base + majoration éventuelle)

                // On vérifie si ce taux correspond à un standard ou 70+ d'une prestation spécifique
                // (En supposant pas de majoration sur la première ligne pour la détection simple, ou alors on prend le tarifBase du calcul s'il est dispo dans le calcul détaillé mais il ne l'est pas explicitement, juste tarifCesuUnitaire)
                // ATTENTION: tarifCesuUnitaire contient la majoration. Si majoration 'none', c'est le taux de base.

                // Plus simple: regardons si on peut mapper le label de la ligne vers une clé de TARIFS_REFERENCE
                const label = firstLine.typePrestation;
                const prestationKey = Object.keys(TARIFS_REFERENCE).find(key => TARIFS_REFERENCE[key].label === label);

                if (prestationKey) {
                  typePrestationSpecifique = TARIFS_REFERENCE[prestationKey];
                  tarifSpecifique = isExonere ? typePrestationSpecifique.plus70 : typePrestationSpecifique.standard;
                }
              }

              // Le tarif à afficher : soit le spécifique trouvé, soit le calculé du contrat, soit le fallback
              const tarifAffiche = tarifSpecifique || devis.calculs?.contrat?.tarifBase || (isExonere ? 17.17 : 24.41);

              return (
                <div className={`devis-section ${isExonere ? 'devis-bg-success' : 'devis-bg-info'}`}>
                  <h4 className="devis-section-title">
                    <Euro size={16} />
                    Type de tarif appliqué
                  </h4>
                  <div className="devis-grid devis-grid-1">
                    <div className="text-center p-4">
                      <div className={`text-2xl font-bold mb-2 ${isExonere ? 'text-green-600' : 'text-blue-600'}`}>
                        {typePrestationSpecifique ? typePrestationSpecifique.label.toUpperCase() : (isExonere ? '70+ (EXONÉRÉ)' : 'STANDARD')}
                      </div>
                      <div className="text-sm text-gray-600 mb-3">
                        Tarif de base : <strong>{formaterEuro(tarifAffiche)}/h</strong>
                        {isExonere && !typePrestationSpecifique && ' (Exonéré 70+)'}
                        {!isExonere && !typePrestationSpecifique && ' (Standard)'}
                        {isExonere && typePrestationSpecifique && ' (Tarif exonéré)'}
                      </div>

                      {isExonere ? (
                        <div className="text-green-600 text-sm">
                          ✅ Tarif exonéré de charges sociales pour bénéficiaires de 70 ans et plus
                        </div>
                      ) : (
                        <div className="text-blue-600 text-sm">
                          📋 Tarif standard avec charges sociales incluses
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Informations du contrat */}
            {devis.calculs?.contrat && (
              <div className="devis-section devis-bg-warning">
                <h4 className="devis-section-title">
                  <FileText size={16} />
                  Paramètres du contrat
                </h4>
                <div className="devis-grid devis-grid-2">
                  <div><strong>Type de contrat:</strong> {devis.calculs.contrat.type || 'Mandat'}</div>
                  <div><strong>Volume hebdomadaire:</strong> {devis.calculs.contrat.volumeHebdo || 'N/A'}h/sem</div>
                  <div><strong>Frais mandat:</strong> {(() => {
                    // Rechercher les frais mandat dans plusieurs sources
                    const fraisMandat = devis.calculs?.contrat?.fraisMandat ||
                      devis.contratParams?.fraisMandat ||
                      (devis.calculs?.contrat?.volumeHebdo ?
                        (devis.calculs.contrat.volumeHebdo <= 10 ? 6.00 :
                          devis.calculs.contrat.volumeHebdo <= 25 ? 5.40 :
                            devis.calculs.contrat.volumeHebdo <= 40 ? 4.20 : 3.00) : 0);
                    return formaterEuro(fraisMandat);
                  })()}/h</div>
                  <div><strong>Date de création:</strong> {(() => {
                    // Rechercher la date dans plusieurs sources
                    const dateCreation = devis.dateCreation ||
                      devis.calculs?.dateGeneration ||
                      devis.createdAt;
                    if (dateCreation) {
                      const date = typeof dateCreation === 'string' ? new Date(dateCreation) : dateCreation;
                      return date.toLocaleDateString('fr-FR');
                    }
                    return 'N/A';
                  })()}</div>
                </div>

                {/* Informations de source du type de tarif pour débogage */}
                {(() => {
                  const sources = [];
                  if (devis.contratParams?.baseRateType) sources.push('Paramètres contrat');
                  if (devis.calculs?.configuration?.baseRateType) sources.push('Configuration calculs');
                  if (devis.calculs?.contrat?.baseRateType) sources.push('Contrat calculs');

                  if (sources.length > 0) {
                    return (
                      <div className="mt-3 pt-3 border-t border-orange-200">
                        <div className="text-xs text-gray-500">
                          <strong>Sources du type de tarif :</strong> {sources.join(', ')}
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>
            )}

            {/* Période de prestation */}
            {(devis.dateDebut || devis.dateFin) && (
              <div className="devis-section devis-bg-info">
                <h4 className="devis-section-title">
                  <Calendar size={16} />
                  Période de prestation
                </h4>
                <div className="devis-grid devis-grid-2">
                  {devis.dateDebut && (
                    <div>
                      <strong>Date de début:</strong>{' '}
                      {(() => {
                        const date = typeof devis.dateDebut.toDate === 'function'
                          ? devis.dateDebut.toDate()
                          : new Date(devis.dateDebut);
                        return date.toLocaleDateString('fr-FR');
                      })()}
                    </div>
                  )}
                  {devis.dateFin && (
                    <div>
                      <strong>Date de fin:</strong>{' '}
                      {(() => {
                        const date = typeof devis.dateFin.toDate === 'function'
                          ? devis.dateFin.toDate()
                          : new Date(devis.dateFin);
                        return date.toLocaleDateString('fr-FR');
                      })()}
                    </div>
                  )}
                  {devis.dateDebut && devis.dateFin && (
                    <div className="col-span-2">
                      <strong>Durée totale:</strong>{' '}
                      {(() => {
                        const debut = typeof devis.dateDebut.toDate === 'function'
                          ? devis.dateDebut.toDate()
                          : new Date(devis.dateDebut);
                        const fin = typeof devis.dateFin.toDate === 'function'
                          ? devis.dateFin.toDate()
                          : new Date(devis.dateFin);
                        return Math.ceil((fin - debut) / (1000 * 60 * 60 * 24));
                      })()} jours
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Détails des prestations - Format identique à l'aperçu de création */}
            {devis.calculs?.lignes && (
              <div className="devis-section devis-bg-warning">
                <h4 className="devis-section-title">
                  <FileText size={16} />
                  Détail des prestations
                </h4>
                <div className="space-y-4">
                  {devis.calculs.lignes.map((ligne, index) => {
                    // Récupérer les données originales pour nbInterventions
                    const ligneOriginale = devis.lignesPrestations?.[index] || {};
                    const nbInterventions = ligneOriginale.nbInterventions || 1;

                    return (
                      <div key={index} className="devis-preview-detail">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <strong className="text-sm">{ligne.typePrestation}</strong>
                            <div className="text-xs text-gray-600">
                              x {ligne.quantite} ({ligne.heuresTotal}h équivalent)
                            </div>
                            {ligne.employeeId && (
                              <div className="text-xs text-blue-600 mt-1">
                                👤 {getPrenomCollaborateur(ligne.employeeId)}
                                {ligne.collaborateurCesu && (
                                  <span className="ml-2 text-gray-600">• CESU: {ligne.collaborateurCesu}</span>
                                )}
                                {ligne.collaborateurNan && (
                                  <span className="ml-2 text-gray-600">• NAN: {ligne.collaborateurNan}</span>
                                )}
                              </div>
                            )}
                            {ligne.majorationAppliquee !== 'Aucune' && (
                              <div className="text-xs text-orange-600 mt-1">
                                ⚡ {ligne.majorationAppliquee}
                              </div>
                            )}
                            {ligne.totalDeplacement > 0 && (
                              <div className="text-xs text-purple-600 mt-1">
                                🚗 {formaterEuro(ligne.fraisDeplacementUnitaire)} par intervention
                              </div>
                            )}
                          </div>
                          <div className="text-right">
                            <div className="text-sm font-semibold">{formaterEuro(ligne.totalLigne)}</div>
                            <div className="text-xs text-gray-600">
                              {formaterEuro(ligne.tarifCesuUnitaire)}/h
                            </div>
                          </div>
                        </div>

                        {/* Détail des coûts */}
                        <div className="grid grid-cols-3 gap-2 text-xs mt-2">
                          <div className="devis-bg-success p-2 rounded">
                            <div>Salaire & charges</div>
                            <div className="font-semibold">{formaterEuro(ligne.totalCesu)}</div>
                          </div>
                          {ligne.totalMandat > 0 && (
                            <div className="devis-bg-info p-2 rounded">
                              <div>Frais mandat</div>
                              <div className="font-semibold">{formaterEuro(ligne.totalMandat)}</div>
                            </div>
                          )}
                          {ligne.totalDeplacement > 0 && (
                            <div className="devis-bg-warning p-2 rounded">
                              <div>Déplacement</div>
                              <div className="font-semibold">{formaterEuro(ligne.totalDeplacement)}</div>
                              <div className="text-xs text-gray-600">
                                {nbInterventions} × {formaterEuro(ligne.fraisDeplacementUnitaire)}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Répartition par collaborateur */}
            {getTotauxParCollaborateur().length > 0 && (
              <div className="devis-section devis-bg-info">
                <h4 className="devis-section-title">
                  <User size={16} />
                  Répartition par collaborateur
                </h4>
                <div className="space-y-3">
                  {getTotauxParCollaborateur().map((collab, index) => (
                    <div key={index} className="devis-preview-detail">
                      <div className="flex justify-between items-center mb-2">
                        <strong className="text-sm">{collab.collaborateur.nom}</strong>
                        <div className="text-sm font-semibold">
                          {formaterEuro(collab.totalCesu + collab.totalMandat + collab.totalDeplacement)}
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-xs">
                        <div>
                          <div>Salaire</div>
                          <div className="font-semibold">{formaterEuro(collab.totalCesu)}</div>
                        </div>
                        {collab.totalMandat > 0 && (
                          <div>
                            <div>Mandat</div>
                            <div className="font-semibold">{formaterEuro(collab.totalMandat)}</div>
                          </div>
                        )}
                        {collab.totalDeplacement > 0 && (
                          <div>
                            <div>Déplacement</div>
                            <div className="font-semibold">{formaterEuro(collab.totalDeplacement)}</div>
                          </div>
                        )}
                      </div>
                      {/* Détail heures par taux */}
                      {collab.detailHeuresParTaux && collab.detailHeuresParTaux.length > 0 && (
                        <div className="text-xs text-gray-700 dark:text-gray-300 mt-2 space-y-1">
                          {collab.detailHeuresParTaux.map((detail, idx) => (
                            <div key={idx} className="flex justify-between items-center pl-2 border-l-2 border-blue-300">
                              <span>
                                {detail.heures}h {detail.majoration !== 'Aucune' ? `${detail.majoration}` : (getBaseRateTypeFromDevis(devis) === '70plus' ? 'Tarif 70+' : 'Tarif Standard')}
                                <span className="text-gray-500 ml-1">({formaterEuro(detail.tauxHoraire)}/h)</span>
                              </span>
                              <span className="font-semibold text-gray-600">{formaterEuro(detail.montantCesu)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="text-xs text-gray-600 mt-2">
                        {collab.totalHeures}h total - {collab.collaborateur.isAutoEntrepreneur ? 'Auto-entrepreneur' : 'Salarié'}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Totaux généraux */}
            {devis.calculs?.totaux && (
              <div className="devis-section devis-bg-success">
                <h4 className="devis-section-title">
                  <Euro size={16} />
                  Synthèse financière
                </h4>
                <div className="devis-grid devis-grid-2">
                  <div><strong>Salaire & charges:</strong> {formaterEuro(devis.calculs.totaux.totalCesu)}</div>
                  <div><strong>Frais mandat:</strong> {formaterEuro(devis.calculs.totaux.totalMandat)}</div>
                  <div><strong>Frais de déplacement:</strong> {formaterEuro(devis.calculs.totaux.totalDeplacement)}</div>
                  <div><strong>Total heures:</strong> {devis.calculs.totaux.totalHeures}h</div>
                  <div className="col-span-2 border-t border-green-200 pt-2 mt-2">
                    <strong>Total TTC:</strong>
                    <span className="ml-2 text-lg font-bold devis-text-success">
                      {formaterEuro(devis.calculs.totaux.totalTTC)}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Informations techniques */}
            {devis.calculs && (
              <div className="devis-section">
                <h4 className="devis-section-title">
                  <FileText size={16} />
                  Informations techniques
                </h4>
                <div className="devis-grid devis-grid-2">
                  <div><strong>Version calcul:</strong> {devis.calculs.configuration?.versionCalcul || '1.0'}</div>
                  <div><strong>Date génération:</strong> {(() => {
                    // Rechercher la date dans plusieurs sources (même logique que pour la date de création)
                    const dateGeneration = devis.calculs?.dateGeneration ||
                      devis.calculs?.configuration?.dateGeneration ||
                      devis.dateCreation ||
                      devis.createdAt;
                    if (dateGeneration) {
                      const date = typeof dateGeneration === 'string' ? new Date(dateGeneration) : dateGeneration;
                      return date.toLocaleString('fr-FR');
                    }
                    return 'N/A';
                  })()}</div>
                  <div><strong>Collaborateurs:</strong> {devis.calculs.nombreCollaborateurs || getCollaborateursUtilises().length || 0}</div>
                  <div><strong>Lignes:</strong> {devis.calculs.nombreLignes || devis.calculs.lignes?.length || 0}</div>
                  {devis.calculs.configuration?.tarifsPersonnalises && (
                    <div className="col-span-2 devis-text-info">
                      ⚡ Tarifs personnalisés appliqués (auto-entrepreneurs)
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Actions */}
            {devis.statut === STATUTS.DEVIS.ENVOYE && (
              <div className="devis-actions">
                <button
                  onClick={() => {
                    handleActionDevis('accepter', devis);
                    onClose();
                  }}
                  className="devis-btn devis-btn-primary"
                >
                  <Check size={16} />
                  Accepter le devis
                </button>
                <button
                  onClick={() => {
                    handleActionDevis('refuser', devis);
                    onClose();
                  }}
                  className="devis-btn devis-btn-secondary"
                >
                  <X size={16} />
                  Refuser le devis
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 dark:border-blue-400 mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Chargement des devis...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-6 max-w-4xl mx-auto">
          <div className="flex items-center">
            <div className="text-red-500 dark:text-red-400 text-2xl mr-3">⚠️</div>
            <div>
              <h3 className="text-lg font-semibold text-red-800 dark:text-red-300">Erreur de chargement</h3>
              <p className="text-red-600 dark:text-red-400 mt-1">{error}</p>
              <button
                onClick={rechargerDonnees}
                className="mt-3 px-4 py-2 bg-red-600 dark:bg-red-700 text-white rounded-lg hover:bg-red-700 dark:hover:bg-red-800 text-sm"
              >
                Réessayer
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* En-tête */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Gestion des devis</h1>
              <p className="text-gray-600 dark:text-gray-400 mt-1">Créez, suivez et gérez vos devis clients</p>
            </div>
            <div className="flex gap-2">

              <button
                className="px-4 py-2 bg-blue-600 dark:bg-blue-700 text-white rounded-lg hover:bg-blue-700 dark:hover:bg-blue-800 flex items-center"
                onClick={() => setShowCreateModal(true)}
              >
                <Plus size={16} className="mr-2" />
                Nouveau devis
              </button>
            </div>
          </div>

          {/* Statistiques rapides */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{devisEnCours.length}</div>
              <div className="text-sm text-blue-800 dark:text-blue-300">En cours</div>
            </div>
            <div className="text-center p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
              <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">{devisEnvoyes.length}</div>
              <div className="text-sm text-orange-800 dark:text-orange-300">Envoyés</div>
            </div>
            <div className="text-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
              <div className="text-2xl font-bold text-green-600 dark:text-green-400">{devisAcceptes.length}</div>
              <div className="text-sm text-green-800 dark:text-green-300">Acceptés</div>
            </div>
            <div className="text-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <div className="text-2xl font-bold text-gray-600 dark:text-gray-300">{devis.length}</div>
              <div className="text-sm text-gray-800 dark:text-gray-400">Total</div>
            </div>
          </div>
        </div>

        {/* Filtres et recherche */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4 mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            {/* Filtres par statut */}
            <div className="flex flex-wrap gap-2">
              {[
                { value: 'all', label: 'Tous', count: devis.length },
                { value: 'brouillon', label: 'Brouillon', count: devisEnCours.length },
                { value: 'envoye', label: 'Envoyés', count: devisEnvoyes.length },
                { value: 'accepte', label: 'Acceptés', count: devisAcceptes.length },
              ].map((filter) => (
                <button
                  key={filter.value}
                  onClick={() => setFilterStatus(filter.value)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${filterStatus === filter.value
                    ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 border border-blue-300 dark:border-blue-600'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                >
                  {filter.label} ({filter.count})
                </button>
              ))}
            </div>

            {/* Barre de recherche */}
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" />
              <input
                type="text"
                placeholder="Rechercher un devis ou client..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {/* Liste des devis */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm">
          {filteredDevis.length === 0 ? (
            <div className="p-12 text-center">
              <FileText size={48} className="mx-auto text-gray-300 dark:text-gray-600 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Aucun devis trouvé</h3>
              <p className="text-gray-500 dark:text-gray-400">
                {searchTerm ? 'Aucun devis ne correspond à votre recherche.' : 'Commencez par créer votre premier devis.'}
              </p>
            </div>
          ) : (
            <>
              {/* Version mobile - Cartes */}
              <div className="md:hidden p-4 space-y-4">
                {filteredDevis.map((devis) => (
                  <div key={devis.id} className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg shadow-sm border border-gray-200 dark:border-gray-600">
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1">
                        <div className="flex items-center mb-1">
                          <FileText size={14} className="text-gray-400 dark:text-gray-500 mr-1" />
                          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{devis.numero}</span>
                        </div>
                        <div className="flex items-center mb-1">
                          <User size={14} className="text-gray-400 dark:text-gray-500 mr-1" />
                          <span className="text-sm text-gray-900 dark:text-gray-100">{devis.clientNom}</span>
                        </div>
                        <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
                          <Calendar size={12} className="mr-1" />
                          {devis.dateCreation ? new Date(devis.dateCreation).toLocaleDateString('fr-FR') : 'N/A'}
                        </div>
                      </div>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatutColor(devis.statut)}`}>
                        {getStatutLabel(devis.statut)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center pt-3 border-t border-gray-200 dark:border-gray-600">
                      <div>
                        <div className="text-lg font-bold text-gray-900 dark:text-gray-100">{formaterEuro(devis.montantTTC)}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">HT: {formaterEuro(devis.montantHT)}</div>
                      </div>
                      <button
                        onClick={() => setDevisAvecActions(devis)}
                        className="px-3 py-2 bg-blue-600 dark:bg-blue-700 text-white rounded-lg hover:bg-blue-700 dark:hover:bg-blue-800 text-sm"
                      >
                        Actions
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Version desktop - Tableau */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full bg-white dark:bg-gray-800">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Devis
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Client
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Montant
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Statut
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {filteredDevis.map((devis) => (
                      <tr key={devis.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <FileText size={16} className="text-gray-400 dark:text-gray-500 mr-2" />
                            <div>
                              <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                {devis.numero}
                              </div>
                              <div className="text-sm text-gray-500 dark:text-gray-400">
                                Validité: {devis.dateValidite ? new Date(devis.dateValidite).toLocaleDateString('fr-FR') : 'N/A'}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <User size={16} className="text-gray-400 dark:text-gray-500 mr-2" />
                            <div>
                              <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                {devis.clientNom}
                              </div>
                              <div className="text-sm text-gray-500 dark:text-gray-400">
                                {devis.clientEmail}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {formaterEuro(devis.montantTTC)}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            HT: {formaterEuro(devis.montantHT)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <Calendar size={16} className="text-gray-400 dark:text-gray-500 mr-2" />
                            <div className="text-sm text-gray-900 dark:text-gray-100">
                              {devis.dateCreation ? new Date(devis.dateCreation).toLocaleDateString('fr-FR') : 'N/A'}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatutColor(devis.statut)}`}>
                            {getStatutLabel(devis.statut)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => setDevisAvecActions(devis)}
                              className="px-3 py-1 bg-blue-600 dark:bg-blue-700 text-white rounded-lg hover:bg-blue-700 dark:hover:bg-blue-800 flex items-center space-x-1 transition-colors"
                              title="Actions disponibles"
                            >
                              <span>Actions</span>
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M6 9l6 6 6-6" />
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        {/* Modal de détail (priorité haute) */}
        {selectedDevis && (
          <DetailDevis
            devis={selectedDevis}
            onClose={() => setSelectedDevis(null)}
          />
        )}

        {/* Panneau d'actions pour un devis spécifique (priorité basse) */}
        {devisAvecActions && (
          <div className={`fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center p-4 ${selectedDevis ? 'z-40' : 'z-50'
            }`}>
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b dark:border-gray-700 flex justify-between items-center sticky top-0 bg-white dark:bg-gray-800 z-10">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">Actions pour le devis {devisAvecActions.numero}</h3>
                <button
                  onClick={() => setDevisAvecActions(null)}
                  className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                >
                  <X size={24} />
                </button>
              </div>
              <div className="p-6">
                <DevisActionsPanel
                  devis={devisAvecActions}
                  onAction={handleActionDevis}
                  userRole="admin"
                  showDetails={true}
                />
              </div>
            </div>
          </div>
        )}

        {/* Modal de création de devis */}
        {showCreateModal && (
          <CreateDevisModal
            onClose={() => setShowCreateModal(false)}
            onSuccess={(newDevis) => {
              setShowCreateModal(false);
              rechargerDonnees();
            }}
          />
        )}

        {/* Modal d'édition de devis */}
        {showEditModal && devisAEditer && (
          <EditDevisModal
            devis={devisAEditer}
            onClose={() => {
              setShowEditModal(false);
              setDevisAEditer(null);
            }}
            onSuccess={(updatedDevis) => {
              setShowEditModal(false);
              setDevisAEditer(null);
              rechargerDonnees();
            }}
          />
        )}


      </div>
    </div>
  );
};

export default DevisManagement;
